import { useEffect, useState, useContext } from "react";
import { DashboardContext } from "../../providers/DashboardProvider";
import { Box, SkeletonText } from "@chakra-ui/react";
import { HospitalDashboardContext } from "../../providers/HospitalProvider";

const improvementDataMapping = {
    "Pinpoint my biggest sources of revenue leakage": {
        key: "chartAccuracyBreakdown",
        info: "Identify the primary sources of revenue leakage by analyzing instances where upcoding or downcoding led to financial loss. Prioritize the highest contributing factor, and if two sources have similar values, highlight both as the top revenue leakages."
    },
    "Show me my most frequently missed codes": {
        key: "chartAccuracyBreakdown",
        info: "Analyze coding errors related to upcoding and downcoding to determine which specific billing codes were most frequently misapplied. Identify the top code with the highest occurrence, and if multiple codes have similar error frequency, list the top two."
    },
    "Identify providers with the most coding gaps": {
        key: "chartAccuracyBreakdown",
        info: "Identify providers with the most documentation or coding gaps"
    },
    "Overall Areas for Improvement": {
        key: "chartAccuracyBreakdown",
        info: "Represents the financial health of the revenue cycle"
    },
};

const AsyncMessageGeneric = ({ option, triggerNextStep }) => {
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const dashboardContext = useContext(DashboardContext);
    const hospitalDashboardContext = useContext(HospitalDashboardContext);

    useEffect(() => {
        const fetchMessage = async () => {
            setLoading(true);
            let responseMessage = "";

            if (!dashboardContext?.filterParams) {
                const msg = "Date filter is missing. Please ensure all data is loaded.";
                setMessage(msg);
                setLoading(false);
                triggerNextStep({ value: msg });
                return;
            }

            const dateFilter = dashboardContext.filterParams
            // const selectedDataKey = improvementDataMapping[option]?.key;
            const info = improvementDataMapping[option]?.info;
            let dataValues = null;

            if (/^Identify providers/i.test(option)) {
                dataValues = dashboardContext.providers;
            }
            else {
                dataValues = hospitalDashboardContext.outstandingRevenue;
            }

            if (!dataValues ||
                (Array.isArray(dataValues) && dataValues.length === 0) ||
                (typeof dataValues === "object" && Object.keys(dataValues).length === 0) ||
                (typeof dataValues === "object" && dataValues?.message === "No data present for this account")) {

                const msg = "Not enough information to generate results for the selected option.";
                setMessage(msg);
                setLoading(false);
                triggerNextStep({ value: msg });
                return;
            }

            let selectedData = {
                data: dataValues,
                info: info
            };

            if (!selectedData.info || !selectedData.data || selectedData.data.length === 0) {
                const msg = "No data available for the selected improvement area.";
                setMessage(msg);
                setLoading(false);
                triggerNextStep({ value: msg });
                return;
            }

            try {
                const responseJson = await dashboardContext.generateAiResponse(option, selectedData, dateFilter);
                responseMessage = responseJson ? JSON.stringify(responseJson) : "No insights available.";
            } catch (error) {
                responseMessage = "Error fetching response. Please try again.";
                console.error("AI Response Error:", error);
            }

            setMessage(responseMessage);
            setLoading(false);
            setTimeout(() => triggerNextStep({ value: message }), 500);
        };

        fetchMessage();
    }, [option]);

    const formatResponse = (response) => {
        let formattedContent = null;
        let responseData;

        try {
            responseData = JSON.parse(response);
        } catch {
            responseData = response;
        }

        try {
            const text = typeof responseData === "object" && responseData.response ? responseData.response : responseData;

            const observationMatch = text.match(/-?\s*Observation:\s*(.*?)(?=-\s*Action:|$)/s);
            const actionMatch = text.match(/-?\s*Action:\s*(.*)/s);

            const observation = observationMatch ? observationMatch[1].trim() : null;
            const action = actionMatch ? actionMatch[1].trim() : null;

            // If neither observation nor action is found, display the original text
            const formattedObservation = observation || "";
            const formattedAction = action || "";

            const formatText = (text) => {
                return text.split("\n").map((line, index) => {
                    line = line.trim();
                    if (line.startsWith("- ") || line.startsWith("• ")) {
                        return <li key={index}>{line.replace(/^-?\s*•?\s*/, "").trim()}</li>;
                    }
                    return <p key={index}>{line}</p>;
                });
            };

            if (!observation && !action) {
                formattedContent = (
                    <div>
                        <p>{text}</p>
                    </div>
                );
            } else {
                formattedContent = (
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textDecoration: 'underline' }}>
                            {option}
                        </div>
                        {formattedObservation && (
                            <>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' }}>Observation:</div>
                                <ul>{formatText(formattedObservation)}</ul>
                            </>
                        )}
                        {formattedAction && (
                            <>
                                <div style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '10px', marginBottom: '5px' }}>Action:</div>
                                <ul>{formatText(formattedAction)}</ul>
                            </>
                        )}
                    </div>
                );
            }

        } catch (error) {
            console.error("Error formatting response:", error);
            formattedContent = <p>Unable to process the response.</p>;
        }

        return formattedContent;
    };


    return (
        <>
            {loading ? (
                <Box textAlign="center" w="100%">
                    <SkeletonText noOfLines={3} spacing="4" w="80%" mx="auto" />
                </Box>
            ) : (
                <div>{message ? formatResponse(message) : null}</div>
            )}
        </>
    );
};

export default AsyncMessageGeneric;